import React, { useEffect, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { ShoppingBag, Loader2, Truck, Package } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const imgSrc = (u) => (!u ? null : u.startsWith("http") ? u : `${API_BASE.replace(/\/api$/, "")}${u}`);

export default function ShowMerch({ showId, className = "" }) {
  const [items, setItems] = useState([]);
  const [cfg, setCfg] = useState({ payments_enabled: true, stripe_enabled: true, paypal_enabled: false });
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("merch_cancelled")) toast("Checkout cancelled", { description: "No charge was made." });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/shows/${showId}/merch`);
        setItems(data.items || []);
        setCfg(data);
      } catch { /* soft-fail: hide section */ }
      finally { setLoading(false); }
    })();
  }, [showId]);

  if (loading || !items.length) return null;

  const setItemSel = (id, patch) =>
    setSel((s) => ({ ...s, [id]: { variant: "", qty: 1, ...(s[id] || {}), ...patch } }));

  const buy = async (item, method) => {
    const s = sel[item.id] || {};
    if (item.variants?.length && !s.variant) { toast.error("Please pick an option first"); return; }
    setBusy(`${item.id}:${method}`);
    try {
      const endpoint = method === "paypal" ? "/merch/paypal/checkout" : "/merch/checkout";
      const { data } = await api.post(endpoint, {
        item_id: item.id, variant: s.variant || "", quantity: s.qty || 1,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else { toast.error("Could not start checkout"); setBusy(""); }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
      setBusy("");
    }
  };

  return (
    <div className={className} data-testid="show-merch">
      <div className="flex items-center gap-3 mb-8">
        <ShoppingBag className="text-[var(--ff-red)]" size={24} />
        <h2 className="font-display text-4xl">Merch</h2>
      </div>

      {!cfg.payments_enabled && (
        <div className="surface-card p-4 mb-6 text-yellow-300 text-sm font-mono-ui" data-testid="merch-paused-banner">
          The store is temporarily paused. Check back soon!
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((item) => {
          const s = sel[item.id] || { variant: "", qty: 1 };
          const showStripe = cfg.payments_enabled && cfg.stripe_enabled && !item.sold_out;
          const showPayPal = cfg.payments_enabled && cfg.paypal_enabled && !item.sold_out;
          const busyStripe = busy === `${item.id}:stripe`;
          const busyPaypal = busy === `${item.id}:paypal`;
          return (
            <div key={item.id} className="surface-card overflow-hidden flex flex-col" data-testid={`merch-item-${item.id}`}>
              <div className="aspect-square bg-[var(--ff-bg)] overflow-hidden flex items-center justify-center">
                {imgSrc(item.image_url)
                  ? <img src={imgSrc(item.image_url)} alt={item.name} className="w-full h-full object-cover" />
                  : <Package size={48} className="text-[var(--ff-text-dim)]" />}
              </div>
              <div className="p-5 flex flex-col gap-3 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display text-xl leading-tight">{item.name}</h3>
                  <div className="font-display text-xl text-[var(--ff-red)] whitespace-nowrap">{money(item.price)}</div>
                </div>
                {item.description && <p className="text-[var(--ff-text-dim)] text-sm">{item.description}</p>}
                <div className="text-[0.6rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)] flex items-center gap-1">
                  {item.requires_shipping ? <><Truck size={11} /> Ships to you</> : <>Digital item</>}
                </div>

                {item.sold_out ? (
                  <div className="mt-auto label-eyebrow text-[var(--ff-red)]" data-testid={`merch-soldout-${item.id}`}>● Sold Out</div>
                ) : (
                  <div className="mt-auto space-y-3 pt-1">
                    {item.variants?.length > 0 && (
                      <select
                        value={s.variant}
                        onChange={(e) => setItemSel(item.id, { variant: e.target.value })}
                        className="w-full px-3 py-2 text-sm bg-[var(--ff-bg)] border border-[var(--ff-border)] rounded-md"
                        data-testid={`merch-variant-${item.id}`}
                      >
                        <option value="">Choose an option…</option>
                        {item.variants.map((v) => (
                          <option key={v.label} value={v.label} disabled={v.sold_out}>
                            {v.label}{v.sold_out ? " — sold out" : ""}
                          </option>
                        ))}
                      </select>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="label-eyebrow">Qty</span>
                      <input
                        type="number" min={1} max={10} value={s.qty || 1}
                        onChange={(e) => setItemSel(item.id, { qty: Math.max(1, Math.min(10, parseInt(e.target.value) || 1)) })}
                        className="w-20 px-3 py-2 text-sm bg-[var(--ff-bg)] border border-[var(--ff-border)] rounded-md"
                        data-testid={`merch-qty-${item.id}`}
                      />
                    </div>
                    {showStripe && (
                      <button onClick={() => buy(item, "stripe")} disabled={busyStripe} className="btn-primary w-full justify-center text-sm" data-testid={`merch-buy-stripe-${item.id}`}>
                        {busyStripe ? <Loader2 size={14} className="animate-spin" /> : <ShoppingBag size={14} />} Buy with Card
                      </button>
                    )}
                    {showPayPal && (
                      <button onClick={() => buy(item, "paypal")} disabled={busyPaypal} className="btn-ghost w-full justify-center text-sm border border-[var(--ff-border)]" data-testid={`merch-buy-paypal-${item.id}`}>
                        {busyPaypal ? <Loader2 size={14} className="animate-spin" /> : null} Buy with PayPal
                      </button>
                    )}
                    {!showStripe && !showPayPal && cfg.payments_enabled && (
                      <div className="text-xs text-[var(--ff-text-dim)] font-mono-ui">Checkout unavailable right now.</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
